/*
Fullscreen photo lightbox. Opens over the current screen, attached to
document.body so it escapes any clipping ancestor. Tap-to-close,
left/right swipe (or arrow keys on desktop) to navigate within the
provided photo group.

Why a single function instead of a stateful component:
- The lightbox is short-lived — open, view, close. No need for
  long-running state across mounts.
- Each `openLightbox` call mounts a fresh DOM tree and removes it on
  close. Object URLs passed in by the caller are NOT revoked here —
  the caller (Arboretum / SpeciesScreen) owns their lifecycle.
*/

const kSwipeCommitPx: number = 60                 // |dx| past this on release → commit nav
const kSwipeCommitFraction: number = 0.22          // OR |dx| / viewport-width past this → commit (covers small viewports)
const kSwipeAxisLockPx: number = 8                 // intent gate: ignore moves until horizontal travel exceeds this
const kSwipeOpacityFloor: number = 0.45            // figure faded to this much when fully off-screen during drag
const kSnapMs: number = 220                        // duration of the post-release snap animation

export interface LightboxPhoto
{
	url: string
	caption?: string
}

/*
Open the lightbox showing `inPhotos[inStartIndex]`. The lightbox is
self-contained: it builds its DOM, attaches handlers, and removes
itself on close. Returns the close function so callers can dismiss
imperatively (e.g., on screen unmount).
*/
export function openLightbox(inPhotos: LightboxPhoto[], inStartIndex: number = 0): () => void
{
	if (inPhotos.length === 0) return () => { /* nothing to close */ }

	let vIndex = Math.max(0, Math.min(inStartIndex, inPhotos.length - 1))  // clamped current photo index

	const vRoot = document.createElement('div')
	vRoot.className = 'lightbox'
	vRoot.setAttribute('role', 'dialog')
	vRoot.setAttribute('aria-modal', 'true')
	vRoot.innerHTML = `
		<div class="lightbox-backdrop"></div>
		<button class="lightbox-close" aria-label="Fermer">×</button>
		<button class="lightbox-prev" aria-label="Précédente">‹</button>
		<button class="lightbox-next" aria-label="Suivante">›</button>
		<figure class="lightbox-figure">
			<img class="lightbox-image" alt="" />
			<figcaption class="lightbox-caption"></figcaption>
		</figure>
		<div class="lightbox-counter"></div>
	`
	document.body.appendChild(vRoot)
	// Lock body scroll while the lightbox is up — otherwise tapping the
	// backdrop can scroll the underlying screen on iOS.
	const vPrevOverflow = document.body.style.overflow
	document.body.style.overflow = 'hidden'

	const vClose = (): void => {
		document.body.style.overflow = vPrevOverflow
		document.removeEventListener('keydown', vOnKey)
		vRoot.remove()
	}

	const vOnKey = (inEv: KeyboardEvent): void => {
		if (inEv.key === 'Escape') vClose()
		else if (inEv.key === 'ArrowLeft') vNavigate(-1)
		else if (inEv.key === 'ArrowRight') vNavigate(+1)
	}
	document.addEventListener('keydown', vOnKey)

	const vNavigate = (inDelta: number): void => {
		const vNew = vIndex + inDelta
		if (vNew < 0 || vNew >= inPhotos.length) return
		vIndex = vNew
		vPaint()
	}

	const vPaint = (): void => {
		const vPhoto = inPhotos[vIndex]
		const vImg = vRoot.querySelector<HTMLImageElement>('.lightbox-image')
		const vCap = vRoot.querySelector<HTMLElement>('.lightbox-caption')
		const vCounter = vRoot.querySelector<HTMLElement>('.lightbox-counter')
		const vPrev = vRoot.querySelector<HTMLButtonElement>('.lightbox-prev')
		const vNext = vRoot.querySelector<HTMLButtonElement>('.lightbox-next')
		if (vImg) vImg.src = vPhoto.url
		if (vCap) vCap.textContent = vPhoto.caption ?? ''
		if (vCounter) vCounter.textContent = `${vIndex + 1} / ${inPhotos.length}`
		if (vPrev) vPrev.disabled = vIndex === 0
		if (vNext) vNext.disabled = vIndex === inPhotos.length - 1
	}

	vRoot.querySelector('.lightbox-close')?.addEventListener('click', vClose)
	vRoot.querySelector('.lightbox-backdrop')?.addEventListener('click', vClose)
	vRoot.querySelector('.lightbox-prev')?.addEventListener('click', () => vNavigate(-1))
	vRoot.querySelector('.lightbox-next')?.addEventListener('click', () => vNavigate(+1))

	// Touch swipe — finger-follow translate during touchmove, snap on
	// touchend. Three resolutions on release:
	//   - past commit threshold + a neighbour exists → animate fully
	//     off-screen, then re-paint with the new index reset at 0
	//   - past commit threshold but at the boundary → animate back to 0
	//     (no-op nav)
	//   - below threshold → animate back to 0
	// Live opacity falloff during drag mirrors iOS Photos' rubber-band
	// feedback so the user feels what release will do.
	const vFigure = vRoot.querySelector<HTMLElement>('.lightbox-figure')
	let vTouchStartX: number | null = null  // touch origin x, null when no drag in progress
	let vTouchStartY: number | null = null  // touch origin y, used for the axis-lock decision
	let vDragLocked: boolean = false  // true once we've decided this gesture is horizontal
	let vDx: number = 0  // current signed delta in px; positive = right swipe (toward prev)

	const vSetTransform = (inDx: number, inAnimate: boolean): void => {
		if (!vFigure) return
		vFigure.style.transition = inAnimate ? `transform ${kSnapMs}ms ease, opacity ${kSnapMs}ms ease` : 'none'
		vFigure.style.transform = `translateX(${inDx}px)`
		const vWidth = vFigure.offsetWidth || 1
		const vRatio = Math.min(1, Math.abs(inDx) / vWidth)  // 0 at rest, 1 fully off-screen
		vFigure.style.opacity = String(1 - vRatio * (1 - kSwipeOpacityFloor))
	}

	const vResetTransform = (inAnimate: boolean): void => { vSetTransform(0, inAnimate) }

	vFigure?.addEventListener('touchstart', (inEv: TouchEvent) => {
		const vT = inEv.touches[0]
		if (!vT) return
		vTouchStartX = vT.clientX
		vTouchStartY = vT.clientY
		vDragLocked = false
		vDx = 0
		vSetTransform(0, false)  // cancel any in-flight snap animation
	}, { passive: true })

	vFigure?.addEventListener('touchmove', (inEv: TouchEvent) => {
		if (vTouchStartX === null || vTouchStartY === null) return
		const vT = inEv.touches[0]
		if (!vT) return
		const vRawDx = vT.clientX - vTouchStartX
		const vRawDy = vT.clientY - vTouchStartY
		// Axis lock: only treat the gesture as a horizontal swipe once
		// it's clearly horizontal. Keeps vertical scroll attempts from
		// being hijacked, and keeps short taps from registering at all.
		if (!vDragLocked)
		{
			if (Math.abs(vRawDx) < kSwipeAxisLockPx && Math.abs(vRawDy) < kSwipeAxisLockPx) return
			if (Math.abs(vRawDy) > Math.abs(vRawDx))
			{
				// Vertical intent — give up on horizontal handling for
				// this gesture so the browser can scroll if it wants.
				vTouchStartX = null
				vTouchStartY = null
				return
			}
			vDragLocked = true
		}
		// Soft rubber-band at the boundaries: at index 0 (no prev), a
		// rightward drag attenuates by half so it feels resistive.
		const vAtLeftEdge = vIndex === 0 && vRawDx > 0
		const vAtRightEdge = vIndex === inPhotos.length - 1 && vRawDx < 0
		vDx = (vAtLeftEdge || vAtRightEdge) ? vRawDx * 0.4 : vRawDx
		// Stop the page from interpreting this as a back-navigation
		// gesture / scroll while we own the drag.
		if (inEv.cancelable) inEv.preventDefault()
		vSetTransform(vDx, false)
	}, { passive: false })

	vFigure?.addEventListener('touchend', () => {
		if (vTouchStartX === null) { vDx = 0; return }
		const vWidth = vFigure?.offsetWidth || 1
		const vCommit = Math.abs(vDx) > kSwipeCommitPx || Math.abs(vDx) / vWidth > kSwipeCommitFraction
		const vTargetDelta = vDx < 0 ? +1 : -1  // dragging left commits to "next" (+1); dragging right to "prev"
		const vNeighbourExists = (vIndex + vTargetDelta) >= 0 && (vIndex + vTargetDelta) < inPhotos.length
		vTouchStartX = null
		vTouchStartY = null

		if (vCommit && vNeighbourExists)
		{
			// Slide the current image off-screen in the drag direction,
			// then on transitionend swap to the new index and reset
			// position without a transition.
			const vExitDx = vDx < 0 ? -vWidth : vWidth
			const vOnEnd = (): void => {
				vFigure?.removeEventListener('transitionend', vOnEnd)
				vIndex = vIndex + vTargetDelta
				vPaint()
				vSetTransform(0, false)  // snap to center with no animation
			}
			vFigure?.addEventListener('transitionend', vOnEnd, { once: true })
			vSetTransform(vExitDx, true)
		}
		else
		{
			// Below threshold or hit the end of the album — bounce back.
			vResetTransform(true)
		}
		vDx = 0
	})

	vFigure?.addEventListener('touchcancel', () => {
		// iOS occasionally fires touchcancel mid-drag (e.g. when a
		// system gesture takes over). Treat it as "release without
		// commit" so the figure snaps back rather than getting stuck.
		vTouchStartX = null
		vTouchStartY = null
		vDx = 0
		vResetTransform(true)
	})

	vPaint()
	return vClose
}
